import { useEffect, useState } from "react";
import { Transition } from "@headlessui/react";
import { MemoryRouter } from "react-router-dom";
import { Icon } from "@circle-react/components/shared/Icon";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { SUPPORT_WIDGET_DEFAULT_TRANSITIONS } from "../constants";
import { useHelpWidgetContext } from "../context/HelpWidgetContext";
import { useCompleteOnboarding } from "../hooks/useCompleteOnboarding";
import { getHistory } from "../utils/locationHistoryManager";
import { FloatingActionButton } from "./FloatingActionButton";
import { OnboardingPopup } from "./OnboardingPopup";
import { Routes } from "./Routes";

export const HelpWidgetMobileBody = () => {
  const {
    isOpen,
    isFabVisible,
    shouldDisplayOnboarding,
    toggleWidget,
    hideFab,
    showFab,
  } = useHelpWidgetContext();
  const [isHovered, setIsHovered] = useState(false);
  const [isWidgetTransitionComplete, setIsWidgetTransitionComplete] =
    useState(!isOpen);
  const [isOnboardingVisible, setIsOnboardingVisible] = useState(
    shouldDisplayOnboarding,
  );

  const { mutate: completeOnboardingQuery } = useCompleteOnboarding();

  const completeOnboarding = () => {
    if (isOnboardingVisible) {
      completeOnboardingQuery();
    }
    setIsOnboardingVisible(false);
  };

  const onFabClick = () => {
    if (isFabVisible) {
      completeOnboarding();
      toggleWidget({ shouldTriggerOnWidgetClose: true });
    }
  };

  const onCloseClick = () => {
    toggleWidget({ shouldTriggerOnWidgetClose: true });
  };

  const hideOnboarding = () => completeOnboarding();

  const initialEntries = getHistory();

  useEffect(() => {
    if (isOpen) {
      setIsWidgetTransitionComplete(false);
    }
  }, [isOpen]);

  const shouldRenderOnboarding =
    !isOpen &&
    isWidgetTransitionComplete &&
    isFabVisible &&
    isOnboardingVisible;

  return (
    <div
      id="help-widget-body"
      className={classNames("fixed bottom-0 right-0 z-[60]", {
        "bottom-20 right-5": !isOpen && isWidgetTransitionComplete,
        "bottom-0 h-full w-full": isOpen,
      })}
    >
      <Transition
        show={isOpen}
        {...SUPPORT_WIDGET_DEFAULT_TRANSITIONS}
        afterLeave={() => setIsWidgetTransitionComplete(true)}
      >
        <div className="relative">
          <MemoryRouter
            initialEntries={initialEntries}
            initialIndex={initialEntries.length - 1}
          >
            <Routes onClose={onCloseClick} />
          </MemoryRouter>
        </div>
      </Transition>

      <div className="flex flex-col">
        {shouldRenderOnboarding && <OnboardingPopup onClick={hideOnboarding} />}
        {!isOpen && isWidgetTransitionComplete && (
          <div
            className="-mr-5 flex justify-end align-baseline"
            onMouseOver={() => setIsHovered(true)}
            onFocus={() => setIsHovered(true)}
            onMouseOut={() => setIsHovered(false)}
            onBlur={() => setIsHovered(false)}
          >
            <div
              className={classNames(
                "transition-transform duration-300 ease-out",
                {
                  "translate-x-[150%]": !isFabVisible,
                },
              )}
            >
              <div className="flex justify-end">
                <FloatingActionButton isOpen={isOpen} onClick={onFabClick} />
              </div>
            </div>
            <Icon
              type={isFabVisible ? "12-chevron-right" : "12-chevron-left"}
              size={12}
              className={classNames("mr-1 !w-4 cursor-pointer text-gray-400", {
                invisible:
                  isOnboardingVisible || isOpen || (isFabVisible && !isHovered),
              })}
              onClick={isFabVisible ? hideFab : showFab}
            />
          </div>
        )}
      </div>
    </div>
  );
};
